import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./SeamPaywall.module.scss"

/**
 * SeamPaywall (`/seam`) — an original, self-contained EXPIRATION / renewal
 * paywall for Private Photo Vault Premium. Paywall type rolled at random for this
 * run: EXPIRATION.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it — privacy told through a
 * gesture the security cluster has never used: a real zipper you draw closed. The
 * private seam that meshes your memories shut.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "Zip it back into private." Your memories live in a soft memory-folio split
 * down the middle. Your term lapsed, so the vault's privacy seam is undone — the
 * two halves sit parted, the photos cooled and unsealed. Nothing is missing;
 * every memory is still on this device. PULL THE ZIP CLOSED and a seam of
 * interlocking light-teeth meshes shut behind the tab, the two panels draw
 * together, each memory warms back to full colour, a lock-charm catches at the
 * base with a click (SEALED · PRIVATE), and your real safeguards come back on
 * watch. Yearly keeps the seam sealed for a whole year; monthly is a 30-day zip.
 *
 * ── Divergence (vs. the 95+ prior concepts, incl. the security cluster) ─────
 * Deliberately far from every security neighbour:
 * Layout      : a memory-FOLIO split by a vertical ZIPPER SEAM — two parted
 *               panels of your own photos with a live zip track between them. Not
 *               a pattern grid (Keyline), rings (Sanctum), a cipher wall (Static),
 *               a darkroom (Safelight), a crest (Ward), a dial (Tumbler) or a
 *               handwheel (Vault Door).
 * Interaction : PULL THE ZIP — grab the pull-tab and draw it down the track; the
 *               teeth mesh in real time behind it and the halves close, drag it
 *               back up to reopen (true two-way zipper physics). New to the whole
 *               set: not a pull-cord (Safelight), drag-slider morph (Bystander),
 *               cipher drag (Static), switch (Standby), press-hold (Ward),
 *               tap-pulse (Vigil) or 2-D pattern trace (Keyline).
 * Metaphor    : the PRIVACY SEAM — what's zipped stays yours; interlocking teeth
 *               as a seam of security.
 * Motion      : MESH — teeth interlock in sequence, the two panels ease together,
 *               memories warm as they're enclosed, a lock-charm drops and clicks,
 *               guards light on watch.
 * Value frame : REINSTATEMENT + privacy-by-closure — calm, intimate, not fearful.
 * Register    : everyday intimacy / small satisfaction — zipping something private.
 * CTA         : "Seal my vault" — yearly = sealed for a full year, featured.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Every safeguard named is a REAL Private Photo Vault feature (only-you lock with
 * pattern / Face ID / code · encrypted Cloud Vault · break-in snapshots · app
 * disguise). The expiration framing states plainly that NOTHING is removed —
 * every memory is still on this device; only the extra protection paused — so
 * there is no data-loss threat. No countdown, no fabricated scarcity, no
 * misleading claim. "Save 79%" is real arithmetic from live product data. Yearly
 * is the featured, default, most prominent price; monthly is small and secondary.
 * The 7-day free trial is the real trial. Renders on built-in debug product data
 * with bundled photos — no native injection, no external egress.
 */

// Two folio panels of memories (bundled, self-contained photos reused from the
// repo). Left column reads top→bottom, then right column.
const LEFT = ["/seam/s1.jpg", "/seam/s2.jpg", "/seam/s3.jpg", "/seam/s4.jpg"]
const RIGHT = ["/seam/s5.jpg", "/seam/s6.jpg", "/seam/s7.jpg", "/seam/s8.jpg"]

// Number of tooth-pairs down the seam.
const N_TEETH = 16

// The real Private Photo Vault safeguards — back "on watch" when the seam seals.
const GUARDS = [
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Only-you lock",
    line: "Pattern, Face ID or a code only you know",
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "a private, encrypted backup of every memory",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-in snapshots",
    line: "a silent photo of anyone who tries a wrong code",
  },
  {
    key: "disguise",
    Icon: svg.featureIcons.Disguise,
    name: "App disguise",
    line: "hide the whole vault behind an ordinary app",
  },
]

function nextYearLabel() {
  const d = new Date()
  const target = new Date(d.getFullYear() + 1, d.getMonth(), 1)
  const month = target.toLocaleString("en-US", { month: "short" })
  return `${month} ${target.getFullYear()}`
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v))

const SeamPaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()

  // Blend the safe-area insets into the private backdrop so the scene runs edge
  // to edge instead of sitting inside pale status-bar bands.
  useEffect(() => {
    actions.setHeaderInsetColor("#0a0c16")
    actions.setFooterInsetColor("#070810")
  }, [actions])

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const throughLabel = useMemo(() => nextYearLabel(), [])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── zip state ─────────────────────────────────────────────────────────────
  // progress 0 = fully open (parted), 1 = fully sealed.
  const [progress, setProgress] = useState(0)
  const progressRef = useRef(0)
  const dragging = useRef(false)
  const userActed = useRef(false)
  const raf = useRef<number | null>(null)

  const sealed = progress >= 0.985

  const trackRef = useRef<HTMLDivElement | null>(null)

  const setProgressVal = useCallback((v: number) => {
    const c = clamp01(v)
    progressRef.current = c
    setProgress(c)
  }, [])

  const stopRaf = useCallback(() => {
    if (raf.current != null) cancelAnimationFrame(raf.current)
    raf.current = null
  }, [])

  // Ease progress toward a target value (used by the auto-demo and the
  // snap-to-seal on release). Cancels if the user grabs the tab mid-animation.
  const animateTo = useCallback(
    (target: number, ms: number, onDone?: () => void) => {
      stopRaf()
      if (reduceMotion) {
        setProgressVal(target)
        onDone?.()
        return
      }
      const from = progressRef.current
      const start = performance.now()
      const ease = (t: number) => 1 - Math.pow(1 - t, 3)
      const step = (now: number) => {
        if (dragging.current) return
        const t = Math.min(1, (now - start) / ms)
        setProgressVal(from + (target - from) * ease(t))
        if (t < 1) raf.current = requestAnimationFrame(step)
        else {
          raf.current = null
          onDone?.()
        }
      }
      raf.current = requestAnimationFrame(step)
    },
    [reduceMotion, setProgressVal, stopRaf],
  )

  // Auto-demo: draw the seam fully closed once so the payoff reads at a glance,
  // then leave it sealed and hand the tab to the user (drag up to reopen).
  useEffect(() => {
    if (window.location.search.includes("nodemo")) return
    const t = window.setTimeout(() => {
      if (!userActed.current) animateTo(1, 1500)
    }, 750)
    return () => {
      window.clearTimeout(t)
      stopRaf()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  // ── manual zip ──────────────────────────────────────────────────────────
  const progressFromPointer = useCallback((clientY: number) => {
    const track = trackRef.current
    if (!track) return progressRef.current
    const r = track.getBoundingClientRect()
    return clamp01((clientY - r.top) / r.height)
  }, [])

  const beginZip = useCallback(
    (e: React.PointerEvent) => {
      userActed.current = true
      stopRaf()
      dragging.current = true
      setProgressVal(progressFromPointer(e.clientY))
      ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
    },
    [progressFromPointer, setProgressVal, stopRaf],
  )

  const moveZip = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging.current) return
      setProgressVal(progressFromPointer(e.clientY))
    },
    [progressFromPointer, setProgressVal],
  )

  const endZip = useCallback(() => {
    if (!dragging.current) return
    dragging.current = false
    const p = progressRef.current
    // Real-zipper feel: near the ends it clicks home; in between it stays put.
    if (p > 0.8) animateTo(1, 320)
    else if (p < 0.12) animateTo(0, 260)
  }, [animateTo])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  // Per-tooth normalized position along the seam (centre of each pair).
  const teeth = useMemo(
    () => Array.from({ length: N_TEETH }, (_, i) => (i + 0.5) / N_TEETH),
    [],
  )

  return (
    <div
      className={`${styles.root} ${sealed ? styles.isSealed : ""}`}
      style={{ ["--p" as any]: progress }}
    >
      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.top}>
          <div className={styles.brand}>
            <span className={styles.brandMark} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="15" height="15">
                <path
                  d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 12.2l2.2 2.2L15 10.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className={styles.brandName}>
              Private Photo Vault
              <span className={styles.brandTag}>Premium</span>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={() => native.postNativeEvent("close")}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* ── intro ──────────────────────────────────────────────────────── */}
        <section className={styles.intro}>
          <span className={styles.kicker}>
            <span className={styles.kickerDot} aria-hidden="true" />
            {sealed ? "Privacy seam · sealed" : "Premium · privacy seam undone"}
          </span>
          <h1 className={styles.title}>
            {sealed ? (
              <>
                Sealed. <span className={styles.accent}>Zipped back</span>
                <br />
                to private.
              </>
            ) : (
              <>
                Your vault fell <span className={styles.dim}>open.</span>
                <br />
                Every memory is still inside.
              </>
            )}
          </h1>
        </section>

        {/* ── the folio + seam (hero) ────────────────────────────────────── */}
        <section className={styles.stage}>
          <div
            className={styles.folio}
            onPointerDown={beginZip}
            onPointerMove={moveZip}
            onPointerUp={endZip}
            onPointerCancel={endZip}
          >
            {/* left / right memory panels that draw together as it seals */}
            <div className={`${styles.panel} ${styles.panelLeft}`}>
              <div className={styles.panelInner}>
                {LEFT.map((src, i) => (
                  <span
                    key={i}
                    className={styles.photo}
                    style={{ backgroundImage: `url(${src})` }}
                  />
                ))}
              </div>
            </div>
            <div className={`${styles.panel} ${styles.panelRight}`}>
              <div className={styles.panelInner}>
                {RIGHT.map((src, i) => (
                  <span
                    key={i}
                    className={styles.photo}
                    style={{ backgroundImage: `url(${src})` }}
                  />
                ))}
              </div>
            </div>

            {/* the open gap behind the seam (private darkness) */}
            <span className={styles.gap} aria-hidden="true" />

            {/* ── the zipper seam ── */}
            <div className={styles.track} ref={trackRef} aria-hidden="true">
              {/* embossed PRIVATE label revealed as it seals */}
              <span className={styles.seamLabel}>
                {"PRIVATE".split("").map((ch, i) => (
                  <span key={i} className={styles.seamChar}>
                    {ch}
                  </span>
                ))}
              </span>

              {teeth.map((t, i) => {
                const meshed = t <= progress
                return (
                  <span
                    key={i}
                    className={`${styles.toothPair} ${meshed ? styles.meshed : ""}`}
                    style={{
                      top: `${t * 100}%`,
                      ["--d" as any]: `${(N_TEETH - i) * 8}ms`,
                    }}
                  >
                    <span className={`${styles.tooth} ${styles.toothL}`} />
                    <span className={`${styles.tooth} ${styles.toothR}`} />
                  </span>
                )
              })}

              {/* the pull-tab / slider that follows the finger */}
              <span
                className={styles.slider}
                style={{ top: `${progress * 100}%` }}
              >
                <span className={styles.sliderBody}>
                  <span className={styles.sliderLock} aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="13" height="13">
                      <rect
                        x="5"
                        y="10.5"
                        width="14"
                        height="10"
                        rx="2.4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                      />
                      <path
                        d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </span>
                <span className={styles.pullTab} aria-hidden="true" />
              </span>
            </div>

            {/* protective frame that glows once sealed */}
            <span
              className={`${styles.frame} ${sealed ? styles.frameOn : ""}`}
              aria-hidden="true"
            />
          </div>

          {/* seal plate */}
          <div
            className={`${styles.plate} ${sealed ? styles.plateOn : styles.plateOff}`}
          >
            <span className={styles.plateDot} />
            {sealed ? "SEALED · PRIVATE" : "OPEN · PROTECTION PAUSED"}
          </div>

          <p className={styles.zipHint}>
            {sealed ? "Pull the tab up to reopen" : "Pull the tab down to seal"}
          </p>
        </section>

        {/* ── reassurance (honest expiration framing) ────────────────────── */}
        <p className={styles.assure}>
          <span className={styles.assureMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Nothing was removed — every memory is still on this device. Renewing
          simply zips your Premium protection back on.
        </p>

        {/* ── guards on watch (real safeguards) ──────────────────────────── */}
        <ul className={styles.guards}>
          <li className={styles.guardsHead}>
            <span>Back on watch</span>
            <span className={styles.guardsCount}>
              {sealed ? `${GUARDS.length}/${GUARDS.length} on` : "paused"}
            </span>
          </li>
          {GUARDS.map((g) => (
            <li
              key={g.key}
              className={`${styles.guard} ${sealed ? styles.guardOn : ""}`}
            >
              <span className={styles.guardIcon} aria-hidden="true">
                <g.Icon width={16} height={16} />
              </span>
              <span className={styles.guardText}>
                <span className={styles.guardName}>{g.name}</span>
                <span className={styles.guardLine}>{g.line}</span>
              </span>
              <span className={styles.guardChip} aria-hidden="true">
                {sealed ? "ON WATCH" : "•"}
              </span>
            </li>
          ))}
        </ul>

        {/* ── pricing: yearly featured, monthly small & secondary ────────── */}
        <div className={styles.plans}>
          <button
            className={`${styles.yearly} ${
              plan === "yearly" ? styles.selected : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.yearlyGlow} aria-hidden="true" />
            <span className={styles.yearlyHead}>
              <span className={styles.yearlyName}>
                Stay sealed · 1 year
                <span className={styles.trialPill}>
                  {trialDays}-day free trial
                </span>
              </span>
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.yearlyPriceRow}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>/ year</span>
              <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
              <span className={styles.check} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
            <span className={styles.yearlyThru}>
              Your seam stays sealed through {throughLabel}
            </span>
          </button>

          <button
            className={`${styles.monthly} ${
              plan === "monthly" ? styles.monthlySel : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span>Or a 30-day zip</span>
            <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
          </button>
        </div>

        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaLabel}>
            {plan === "yearly"
              ? `Seal my vault — ${trialDays}-day free trial`
              : "Seal my vault"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `Then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default SeamPaywall
